/**
 * Watchdog - Modern Analytics & Error Tracking
 * 
 * Clean, modular architecture with Observer and Strategy patterns
 * ClickHouse-optimized event schema for efficient analytics
 */

import { WatchdogCore } from './core/WatchdogCore.js';
import type { WatchdogConfig } from './types.js';
import type { Middleware } from './middleware/types.js';

// Global instance
let watchdogInstance: WatchdogCore | null = null;

/**
 * Initialize Watchdog
 */
export function initWatchdog(config: WatchdogConfig): void {
  if (watchdogInstance) {
    console.warn('[Watchdog] Already initialized. Destroying previous instance.');
    watchdogInstance.destroy();
  }

  watchdogInstance = new WatchdogCore(config);
}

/**
 * Destroy Watchdog and cleanup
 */
export function destroyWatchdog(): void {
  if (watchdogInstance) {
    watchdogInstance.destroy();
    watchdogInstance = null;
  }
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(featureName: string, context: Record<string, any> = {}): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.trackFeatureUsage(featureName, context);
}

/**
 * Track conversion event
 */
export function trackConversion(event: string, value?: number, metadata?: Record<string, any>): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.trackConversion(event, value, metadata);
}

/**
 * Track custom error (deprecated - errors are auto-tracked)
 * Use addCustomBreadcrumb for additional context instead
 */
export function trackCustomError(message: string, context?: Record<string, any>): void {
  console.warn('[Watchdog] trackCustomError is deprecated. Errors are tracked automatically. Use addCustomBreadcrumb() for context.');
  addCustomBreadcrumb(`Custom error: ${message}`, context);
}

/**
 * Add a custom breadcrumb for debugging context
 */
export function addCustomBreadcrumb(message: string, data?: Record<string, any>): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.addCustomBreadcrumb(message, data);
}

/**
 * Force flush all buffered events
 */
export function forceFlush(): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.flush();
}

/**
 * Get analytics status
 */
export function getAnalyticsStatus(): {
  isRecording: boolean;
  sessionId: string;
  bufferSize: number;
  collectors: string[];
} | null {
  if (!watchdogInstance) {
    return null;
  }

  return watchdogInstance.getStatus();
}

/**
 * Pause session recording
 */
export function pauseRecording(): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.pauseRecording();
}

/**
 * Resume session recording
 */
export function resumeRecording(): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.resumeRecording();
}

/**
 * Update user ID
 */
export function updateUserId(newUserId: string): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.updateUserId(newUserId);
}

/**
 * Register a custom middleware
 * 
 * @example
 * ```typescript
 * registerMiddleware({
 *   name: 'my-enricher',
 *   onPostCollect(event) {
 *     return { ...event, customField: 'value' };
 *   }
 * });
 * ```
 */
export function registerMiddleware(middleware: Middleware): void {
  if (!watchdogInstance) {
    console.warn('[Watchdog] Not initialized. Call initWatchdog() first.');
    return;
  }

  watchdogInstance.registerMiddleware(middleware);
}

// Export types for TypeScript users
export type { WatchdogConfig, Breadcrumb, ConnectionInfo, Severity } from './types.js';
export type { Middleware, CollectedEvent, EventBatch } from './middleware/types.js';
export type { EventMetadata } from './models/BaseEvent.js';
export type { ErrorEvent } from './models/ErrorEvent.js';
export type { WebVitalEvent } from './models/WebVitalEvent.js';
export type { PerformanceEvent } from './models/PerformanceEvent.js';
export type { BehaviorEvent } from './models/BehaviorEvent.js';
export type { SessionEvent } from './models/SessionEvent.js';

// Export strategy interfaces for advanced users
export type { SeverityStrategy } from './strategies/severity/SeverityStrategy.js';
export type { TransportStrategy } from './strategies/transport/TransportStrategy.js';
